import { DOCUMENT } from '@angular/common';
import {
  afterRenderEffect, Component, computed, ElementRef, inject, input, viewChild
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';

import { PriceFilter } from '../../model/price-filter';
import { ProductFilter } from '../../model/product-filter';
import { StorefrontContext } from '../../service/storefront-context';
import { WebsiteService } from '../../service/website.service';
import { TagItemComponent } from './tag-item.component';

const LABELS = ['value-lower', 'value-upper'];

/** noUiSlider hangs its API off the element it took over, and that is how it is handed back. */
type SliderElement = HTMLElement & { noUiSlider?: NoUiSlider };

/**
 * The `products-filter` partial: search, categories, the price range and the parameter tags.
 *
 * The price range is the vendor noUiSlider the Mustache page loaded inline. It is rebuilt after
 * render whenever the filter changes, and on submit rewrites `priceFrom`/`priceTo` in the
 * query string.
 */
@Component({
  selector: 'products-filter',
  imports: [RouterLink, TagItemComponent],
  host: {
    style: 'display: contents'
  },
  template: `
    <div class="leftbar p-r-20 p-r-0-sm product-filters">
      <form role="search" (submit)="search($event)">
        <div class="search-product pos-relative bo4 of-hidden m-b-20">
          <input #searchBox class="s-text7 size6 p-l-23 p-r-50" name="search"
            placeholder="Знайти товари..." [value]="searchText() ?? ''">

          <button type="submit" class="flex-c-m size5 ab-r-m color2 color0-hov trans-0-4">
            <i class="fs-12 fa fa-search" aria-hidden="true"></i>
          </button>
        </div>
      </form>
      @if (categories().length > 1) {
        <div class="wrap-dropdown-content p-t-15 p-b-14">
          <h5 class="js-toggle-dropdown-content flex-sb-m cs-pointer m-text14 color0-hov trans-0-4">
            Категорії
            <i class="down-mark fs-12 color1 fa fa-minus dis-none" aria-hidden="true"></i>
            <i class="up-mark fs-12 color1 fa fa-plus" aria-hidden="true"></i>
          </h5>

          <div class="dropdown-content dis-none p-t-15 p-b-23">
            <ul>
              <li class="p-t-4">
                <a routerLink="/products" class="s-text13 active1">
                  Всі
                </a>
              </li>

              @for (category of categories(); track $index) {
                <li class="p-t-4 category">
                  <a class="s-text13" [routerLink]="'/' + category.code">
                    {{category.name}}
                  </a>
                </li>
              }
            </ul>
          </div>
        </div>
      }

      @if (filter(); as productFilter) {
        <div class="wrap-dropdown-content p-t-15 p-b-14"
          [class.active-dropdown-content]="productFilter.activeFilter">
          <h5 class="js-toggle-dropdown-content flex-sb-m cs-pointer m-text14 color0-hov trans-0-4">
            Фільтри
            <i class="down-mark fs-12 color1 fa fa-minus dis-none" aria-hidden="true"></i>
            <i class="up-mark fs-12 color1 fa fa-plus" aria-hidden="true"></i>
          </h5>

          <div class="dropdown-content dis-none p-t-15 p-b-23">
            @if (productFilter.priceFilter) {
              <div class="filter-price p-t-22 p-b-25">
                <div class="m-text15 p-b-17">
                  Ціна
                </div>

                <div class="wra-filter-bar">
                  <div #filterBar id="filter-bar"></div>
                </div>

                <div class="flex-sb-m flex-w p-t-16">
                  <div class="w-size6">
                    <!-- Button -->
                    <button id="filter-price-submit" class="flex-c-m size4 bg7 bo-rad-15 hov1 s-text14 trans-0-4"
                      (click)="submitPrice()">
                      Оновити
                    </button>
                  </div>

                  <div class="s-text3 p-t-10 p-b-10">
                    від <span id="value-lower"></span> до <span id="value-upper"></span> {{context.currency()}}
                  </div>
                </div>
              </div>
            }

            @for (parameterFilter of productFilter.parameterFilters; track $index) {
              <div class="filter filter-color p-t-22 p-b-25 bo3">
                <div class="m-text15 p-b-12">
                  {{parameterFilter.label}}
                </div>

                <ul class="flex-w">
                  @for (item of parameterFilter.items; track $index) {
                    <li class="m-r-10">
                      <tag-item [item]="item" />
                    </li>
                  }
                </ul>
              </div>
            }

            @if (productFilter.availableOnly; as availableOnly) {
              <div class="filter filter-admin p-t-22 p-b-25 bo3">
                <div class="m-text15 p-b-12">
                  Розширені фільтри
                </div>

                <ul class="flex-w">
                  <li class="m-r-10">
                    <tag-item [item]="availableOnly" />
                  </li>
                </ul>
              </div>
            }
          </div>
        </div>
      }
    </div>
    `
})
export class ProductsFilterComponent {

  readonly filter = input<ProductFilter>();
  readonly searchText = input<string>();

  private document = inject(DOCUMENT);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private searchBox = viewChild<ElementRef<HTMLInputElement>>('searchBox');
  private filterBar = viewChild<ElementRef<HTMLElement>>('filterBar');
  private websites = inject(WebsiteService);
  private queue = Promise.resolve();

  readonly categories = computed(() => this.websites.current().categories ?? []);
  readonly context = inject(StorefrontContext);

  constructor() {
    // Runs in the browser only, and again whenever the filter it reads changes. The runs are
    // chained because two of them would otherwise both get past the script load below, and the
    // second `create` throws on an element the first has already taken over.
    afterRenderEffect(() => {
      const price = this.filter()?.priceFilter;
      this.queue = this.queue
        .then(() => this.showSlider(price))
        .catch(error => console.error('Cannot show the price filter', error));
    });
  }

  search(event: Event) {
    event.preventDefault();
    return this.router.navigate(['/products'], { queryParams: { search: this.searchBox()?.nativeElement.value } });
  }

  submitPrice() {
    const [priceFrom, priceTo] = LABELS.map(id => this.document.getElementById(id)?.innerHTML);
    return this.router.navigate([], {
      relativeTo: this.route,
      // Back to the first page: a narrower range may not have as many
      queryParams: { priceFrom, priceTo, page: null },
      queryParamsHandling: 'merge',
      fragment: 'product-list'
    });
  }

  private async showSlider(price: PriceFilter | undefined) {
    const bar = this.filterBar()?.nativeElement as SliderElement | undefined;
    if (!bar) {
      return;
    }
    // The `@if` above keeps this element across a navigation, so whatever slider is on it holds
    // the previous category's range. There is no updating it in place: it is built again from
    // the range the page just brought.
    bar.noUiSlider?.destroy();
    if (!price) {
      return;
    }
    await this.loadSlider();
    const labels = LABELS.map(id => this.document.getElementById(id));
    const slider = noUiSlider.create(bar, {
      start: [price.from, price.to],
      connect: true,
      range: { min: price.min, max: price.max }
    });
    slider.on('update', (values: string[], handle: number) => {
      const label = labels[handle];
      if (label) {
        label.innerHTML = String(Math.round(Number(values[handle])));
      }
    });
  }

  private loadSlider(): Promise<void> {
    if (typeof noUiSlider !== 'undefined') {
      return Promise.resolve();
    }
    const css = this.document.createElement('link');
    css.rel = 'stylesheet';
    css.href = '/fashe/vendor/noui/nouislider.min.css';
    this.document.head.appendChild(css);
    return new Promise((resolve, reject) => {
      const script = this.document.createElement('script');
      script.src = '/fashe/vendor/noui/nouislider.min.js';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Cannot load nouislider'));
      this.document.body.appendChild(script);
    });
  }

}
